import React from 'react';
import { 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  CardHeader, 
  Typography, 
  useTheme,
  Divider,
  IconButton,
  Tooltip,
  Avatar,
  LinearProgress,
  Stack,
  Paper
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import AirIcon from '@mui/icons-material/Air';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  Cell, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  Legend, 
  ResponsiveContainer,
  AreaChart,
  Area,
  ScatterChart,
  Scatter,
  ZAxis,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ComposedChart
} from 'recharts';

// 미니 게이지 차트 컴포넌트
const MiniGaugeChart = ({ value, maxValue, title, color, icon: IconComponent }) => {
  const theme = useTheme();
  const percentage = (value / maxValue) * 100;
  
  // 게이지 색상 결정
  let gaugeColor = color || theme.palette.primary.main;
  
  return (
    <Box sx={{ position: 'relative', p: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        {IconComponent && (
          <Avatar sx={{ bgcolor: `${gaugeColor}20`, mr: 1, width: 28, height: 28 }}>
            <IconComponent fontSize="small" sx={{ color: gaugeColor }} />
          </Avatar>
        )}
        <Typography variant="body2" fontWeight="medium">
          {title}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="h6" fontWeight="bold">
          {value.toFixed(1)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {maxValue} tCO₂e
        </Typography>
      </Box>
      <LinearProgress 
        variant="determinate" 
        value={percentage} 
        sx={{ 
          height: 6, 
          borderRadius: 3,
          bgcolor: `${gaugeColor}20`,
          '& .MuiLinearProgress-bar': {
            bgcolor: gaugeColor,
            borderRadius: 3
          }
        }} 
      />
    </Box>
  );
};

// 게이지 차트 컴포넌트
const GaugeChart = ({ value, maxValue, title, color }) => {
  const theme = useTheme();
  const percentage = (value / maxValue) * 100;
  
  // 게이지 색상 결정
  let gaugeColor = theme.palette.success.main;
  if (percentage > 80) {
    gaugeColor = theme.palette.error.main;
  } else if (percentage > 50) {
    gaugeColor = theme.palette.warning.main;
  }
  
  return (
    <Box sx={{ position: 'relative', textAlign: 'center', pt: 1 }}>
      <Box sx={{ position: 'relative', display: 'inline-block' }}>
        <svg width="120" height="80" viewBox="0 0 120 60">
          <path
            d="M 10 50 A 50 50 0 0 1 110 50"
            fill="none"
            stroke={theme.palette.background.lighter}
            strokeWidth="8"
            strokeLinecap="round"
          />
          <path
            d="M 10 50 A 50 50 0 0 1 110 50"
            fill="none"
            stroke={color || gaugeColor}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray="157"
            strokeDashoffset={157 - (157 * percentage) / 100}
          />
        </svg>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -10%)',
            textAlign: 'center',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: color || gaugeColor }}>
            {value.toFixed(1)}
          </Typography>
        </Box>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
        {title}
      </Typography>
    </Box>
  );
};

// 스파크라인 컴포넌트
const SparkLine = ({ data, dataKey, color, height = 40, width = '100%' }) => {
  const theme = useTheme();
  
  return (
    <Box sx={{ height, width }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data}>
          <Line 
            type="monotone" 
            dataKey={dataKey} 
            stroke={color || theme.palette.primary.main} 
            strokeWidth={2}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

// 미니 바 차트 컴포넌트
const MiniBarChart = ({ data, dataKey, color, height = 40, width = '100%' }) => {
  const theme = useTheme();
  
  return (
    <Box sx={{ height, width }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} barSize={4}>
          <Bar 
            dataKey={dataKey} 
            fill={color || theme.palette.primary.main} 
            radius={[2, 2, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

// 대시보드 페이지
const DashboardPage = () => {
  const theme = useTheme();
  
  // 차트 데이터
  const emissionTrendData = [
    { name: '1월', 배출량: 12.4 },
    { name: '2월', 배출량: 15.7 },
    { name: '3월', 배출량: 14.2 },
    { name: '4월', 배출량: 13.8 },
    { name: '5월', 배출량: 12.9 },
    { name: '6월', 배출량: 11.5 },
    { name: '7월', 배출량: 10.8 },
    { name: '8월', 배출량: 11.2 },
    { name: '9월', 배출량: 12.7 },
    { name: '10월', 배출량: 13.5 },
    { name: '11월', 배출량: 14.8 },
    { name: '12월', 배출량: 16.2 },
  ];
  
  const emissionSourceData = [
    { name: '교통', value: 35 },
    { name: '전기', value: 25 },
    { name: '난방', value: 20 },
    { name: '식품', value: 15 },
    { name: '기타', value: 5 },
  ];
  
  const COLORS = [
    theme.palette.chart.c1,
    theme.palette.chart.c2,
    theme.palette.chart.c3,
    theme.palette.chart.c4,
    theme.palette.chart.c5,
  ];
  
  const activityComparisonData = [
    { name: '교통', 이번달: 28.8, 지난달: 33.0 },
    { name: '전기', 이번달: 12.0, 지난달: 12.0 },
    { name: '난방', 이번달: 8.0, 지난달: 15.0 },
    { name: '식품', 이번달: 6.0, 지난달: 6.5 },
    { name: '기타', 이번달: 3.0, 지난달: 3.5 },
  ];
  
  const weeklyTrendData = [
    { day: '월', 배출량: 3.2, 목표: 3.5 },
    { day: '화', 배출량: 3.8, 목표: 3.5 },
    { day: '수', 배출량: 3.1, 목표: 3.5 },
    { day: '목', 배출량: 3.3, 목표: 3.5 },
    { day: '금', 배출량: 3.9, 목표: 3.5 },
    { day: '토', 배출량: 2.5, 목표: 3.0 },
    { day: '일', 배출량: 2.2, 목표: 3.0 },
  ];
  
  const yearlyComparisonData = [
    { month: '1월', 올해: 12.4, 작년: 13.2 },
    { month: '2월', 올해: 15.7, 작년: 14.8 },
    { month: '3월', 올해: 14.2, 작년: 15.1 },
    { month: '4월', 올해: 13.8, 작년: 14.5 },
    { month: '5월', 올해: 12.9, 작년: 13.7 },
    { month: '6월', 올해: 11.5, 작년: 12.8 },
    { month: '7월', 올해: 10.8, 작년: 12.2 },
    { month: '8월', 올해: 11.2, 작년: 12.0 },
    { month: '9월', 올해: 12.7, 작년: 13.5 },
    { month: '10월', 올해: 13.5, 작년: 14.2 },
    { month: '11월', 올해: 14.8, 작년: 15.5 },
    { month: '12월', 올해: 16.2, 작년: 17.0 },
  ];
  
  const scatterData = [
    { x: 12, y: 23, z: 10, name: '1월' },
    { x: 15, y: 25, z: 12, name: '2월' },
    { x: 18, y: 22, z: 8, name: '3월' },
    { x: 14, y: 18, z: 15, name: '4월' },
    { x: 10, y: 15, z: 13, name: '5월' },
    { x: 8, y: 12, z: 9, name: '6월' },
  ];
  
  const radarData = [
    { subject: '교통', A: 80, B: 90, fullMark: 100 },
    { subject: '전기', A: 60, B: 85, fullMark: 100 },
    { subject: '난방', A: 75, B: 50, fullMark: 100 },
    { subject: '식품', A: 40, B: 45, fullMark: 100 },
    { subject: '기타', A: 30, B: 25, fullMark: 100 },
  ];
  
  const sparklineData = [
    { value: 10 },
    { value: 15 },
    { value: 12 },
    { value: 18 },
    { value: 14 },
    { value: 16 },
    { value: 13 },
    { value: 17 },
    { value: 15 },
    { value: 19 },
  ];
  
  const miniBarData = [
    { value: 8 },
    { value: 12 },
    { value: 5 },
    { value: 15 },
    { value: 10 },
    { value: 7 },
    { value: 13 },
    { value: 9 },
    { value: 11 },
    { value: 14 },
  ];
  
  const composedChartData = [
    { name: '1월', 배출량: 12.4, 목표: 13.0, 감축량: 0.6 },
    { name: '2월', 배출량: 15.7, 목표: 13.0, 감축량: -2.7 },
    { name: '3월', 배출량: 14.2, 목표: 13.0, 감축량: -1.2 },
    { name: '4월', 배출량: 13.8, 목표: 13.0, 감축량: -0.8 },
    { name: '5월', 배출량: 12.9, 목표: 13.0, 감축량: 0.1 },
    { name: '6월', 배출량: 11.5, 목표: 13.0, 감축량: 1.5 },
  ];
  
  return (
    <Box sx={{ flexGrow: 1, py: 0 }}>
      {/* <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
        탄소 발자국 대시보드
      </Typography> */}
      
      {/* 상단 요약 카드 */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">이번 달 총 배출량</Typography>
                <Tooltip title="이번 달 탄소 배출량 총합입니다">
                  <IconButton size="small">
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  58.7
                </Typography>
                <Typography variant="body2" sx={{ ml: 1, mb: 0.5 }}>
                  tCO₂e
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <ArrowDownwardIcon sx={{ color: theme.palette.success.main, fontSize: 16, mr: 0.5 }} />
                <Typography variant="body2" sx={{ color: theme.palette.success.main }}>
                  7.2% 감소
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>
                  지난 달 대비
                </Typography>
              </Box>
              <SparkLine 
                data={emissionTrendData.slice(-6)} 
                dataKey="배출량" 
                color={theme.palette.primary.main} 
                height={30} 
              />
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">연간 예상 배출량</Typography>
                <Tooltip title="현재 추세 기준 연간 예상 배출량입니다">
                  <IconButton size="small">
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  683.4
                </Typography>
                <Typography variant="body2" sx={{ ml: 1, mb: 0.5 }}>
                  tCO₂e
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <ArrowUpwardIcon sx={{ color: theme.palette.error.main, fontSize: 16, mr: 0.5 }} />
                <Typography variant="body2" sx={{ color: theme.palette.error.main }}>
                  2.5% 증가
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>
                  작년 대비
                </Typography>
              </Box>
              <MiniBarChart 
                data={miniBarData} 
                dataKey="value" 
                color={theme.palette.chart.c2} 
                height={30} 
              />
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', minHeight: 160 }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">목표 달성률</Typography>
                <Tooltip title="연간 탄소 배출량 감축 목표 달성률입니다">
                  <IconButton size="small">
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  68%
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <Typography variant="caption" sx={{ color: theme.palette.primary.main }}>
                  목표: 750 tCO₂e 이하
                </Typography>
              </Box>
              <Box sx={{ mt: 1 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={68} 
                  sx={{ 
                    height: 8, 
                    borderRadius: 4,
                    bgcolor: `${theme.palette.primary.main}20`,
                    '& .MuiLinearProgress-bar': {
                      bgcolor: theme.palette.primary.main,
                      borderRadius: 4
                    }
                  }} 
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', minHeight: 160 }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">탄소 중립 진행도</Typography>
                <Tooltip title="탄소 중립 목표 달성 진행도입니다">
                  <IconButton size="small">
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  42%
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <TrendingUpIcon sx={{ color: theme.palette.success.main, fontSize: 16, mr: 0.5 }} />
                <Typography variant="body2" sx={{ color: theme.palette.success.main }}>
                  5.3% 개선
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>
                  지난 분기 대비
                </Typography>
              </Box>
              <Box sx={{ mt: 1 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={42} 
                  sx={{ 
                    height: 8, 
                    borderRadius: 4,
                    bgcolor: `${theme.palette.chart.c3}20`,
                    '& .MuiLinearProgress-bar': {
                      bgcolor: theme.palette.chart.c3,
                      borderRadius: 4
                    }
                  }} 
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* 주요 배출원 미니 카드 */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper sx={{ p: 1.5, bgcolor: `${theme.palette.chart.c1}10` }}>
            <MiniGaugeChart 
              value={28.8} 
              maxValue={40} 
              title="교통 배출량" 
              color={theme.palette.chart.c1}
              icon={DirectionsCarIcon}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper sx={{ p: 1.5, bgcolor: `${theme.palette.chart.c2}10` }}>
            <MiniGaugeChart 
              value={12.0} 
              maxValue={20} 
              title="전기 사용량" 
              color={theme.palette.chart.c2}
              icon={ElectricBoltIcon}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper sx={{ p: 1.5, bgcolor: `${theme.palette.chart.c3}10` }}>
            <MiniGaugeChart 
              value={8.0} 
              maxValue={15} 
              title="난방 사용량" 
              color={theme.palette.chart.c3}
              icon={LocalGasStationIcon}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper sx={{ p: 1.5, bgcolor: `${theme.palette.chart.c4}10` }}>
            <MiniGaugeChart 
              value={6.0} 
              maxValue={10} 
              title="식품 소비량" 
              color={theme.palette.chart.c4}
              icon={RestaurantIcon}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper sx={{ p: 1.5, bgcolor: `${theme.palette.chart.c5}10` }}>
            <MiniGaugeChart 
              value={3.0} 
              maxValue={5} 
              title="기타 배출량" 
              color={theme.palette.chart.c5}
              icon={AirIcon}
            />
          </Paper>
        </Grid>
      </Grid>
      
      {/* 차트 섹션 - 첫 번째 행 */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {/* 배출량 추이 차트 */}
        <Grid item xs={12} md={8}>
          <Card sx={{ height: '100%' }}>
            <CardHeader 
              title="월별 탄소 배출량 추이" 
              titleTypographyProps={{ variant: 'subtitle1', fontWeight: 'medium' }}
              action={
                <IconButton aria-label="settings" size="small">
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              }
              sx={{ p: 2, pb: 1 }}
            />
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ height: 240 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={composedChartData}
                    margin={{ top: 15, right: 30, left: 20, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.background.lighter} />
                    <XAxis 
                      dataKey="name" 
                      tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                      axisLine={{ stroke: theme.palette.divider }}
                    />
                    <YAxis 
                      yAxisId="left"
                      tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                      axisLine={{ stroke: theme.palette.divider }}
                      unit=" tCO₂e"
                    />
                    <YAxis 
                      yAxisId="right"
                      orientation="right"
                      tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                      axisLine={{ stroke: theme.palette.divider }}
                      unit=" tCO₂e"
                    />
                    <RechartsTooltip 
                      contentStyle={{ 
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`
                      }}
                    />
                    <Legend />
                    <Area
                      yAxisId="left"
                      type="monotone"
                      dataKey="배출량"
                      fill={`${theme.palette.primary.main}40`}
                      stroke={theme.palette.primary.main}
                      strokeWidth={2}
                    />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="목표"
                      stroke={theme.palette.chart.c3}
                      strokeWidth={2}
                      strokeDasharray="5 5"
                    />
                    <Bar
                      yAxisId="right"
                      dataKey="감축량"
                      fill={theme.palette.chart.c2}
                      radius={[4, 4, 0, 0]}
                      barSize={20}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* 배출원 비율 차트 */}
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardHeader 
              title="배출원 비율" 
              titleTypographyProps={{ variant: 'subtitle1', fontWeight: 'medium' }}
              action={
                <IconButton aria-label="settings" size="small">
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              }
              sx={{ p: 2, pb: 1 }}
            />
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ height: 240, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={emissionSourceData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={80}
                      innerRadius={40}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {emissionSourceData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip 
                      contentStyle={{ 
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* 차트 섹션 - 두 번째 행 */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {/* 주간 배출량 차트 */}
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardHeader 
              title="주간 배출량 추이" 
              titleTypographyProps={{ variant: 'subtitle1', fontWeight: 'medium' }}
              action={
                <IconButton aria-label="settings" size="small">
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              }
              sx={{ p: 2, pb: 1 }}
            />
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ height: 240 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={weeklyTrendData}
                    margin={{ top: 10, right: 30, left: 20, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.background.lighter} />
                    <XAxis 
                      dataKey="day" 
                      tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                      axisLine={{ stroke: theme.palette.divider }}
                    />
                    <YAxis 
                      tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                      axisLine={{ stroke: theme.palette.divider }}
                      unit=" tCO₂e"
                    />
                    <RechartsTooltip 
                      contentStyle={{ 
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`
                      }}
                    />
                    <Legend />
                    <Bar dataKey="배출량" fill={theme.palette.primary.main} radius={[4, 4, 0, 0]} barSize={20} />
                    <Line 
                      type="monotone" 
                      dataKey="목표" 
                      stroke={theme.palette.chart.c3} 
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={false}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* 연간 비교 차트 */}
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardHeader 
              title="연간 배출량 비교" 
              titleTypographyProps={{ variant: 'subtitle1', fontWeight: 'medium' }}
              action={
                <IconButton aria-label="settings" size="small">
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              }
              sx={{ p: 2, pb: 1 }}
            />
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ height: 240 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={yearlyComparisonData.slice(0, 6)}
                    margin={{ top: 15, right: 30, left: 20, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.background.lighter} />
                    <XAxis 
                      dataKey="month" 
                      tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                      axisLine={{ stroke: theme.palette.divider }}
                    />
                    <YAxis 
                      tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                      axisLine={{ stroke: theme.palette.divider }}
                      unit=" tCO₂e"
                    />
                    <RechartsTooltip 
                      contentStyle={{ 
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`
                      }}
                    />
                    <Legend />
                    <Area 
                      type="monotone" 
                      dataKey="올해" 
                      stroke={theme.palette.primary.main} 
                      fill={`${theme.palette.primary.main}40`}
                      strokeWidth={2}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="작년" 
                      stroke={theme.palette.chart.c2} 
                      fill={`${theme.palette.chart.c2}40`}
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* 레이더 차트 */}
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardHeader 
              title="배출원 효율성 분석" 
              titleTypographyProps={{ variant: 'subtitle1', fontWeight: 'medium' }}
              action={
                <IconButton aria-label="settings" size="small">
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              }
              sx={{ p: 2, pb: 1 }}
            />
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ height: 240 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart cx="50%" cy="50%" outerRadius={80} data={radarData}>
                    <PolarGrid stroke={theme.palette.background.lighter} />
                    <PolarAngleAxis 
                      dataKey="subject" 
                      tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                    />
                    <PolarRadiusAxis 
                      angle={30} 
                      domain={[0, 100]} 
                      tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                    />
                    <Radar 
                      name="올해" 
                      dataKey="A" 
                      stroke={theme.palette.primary.main} 
                      fill={`${theme.palette.primary.main}60`} 
                      fillOpacity={0.6} 
                    />
                    <Radar 
                      name="작년" 
                      dataKey="B" 
                      stroke={theme.palette.chart.c2} 
                      fill={`${theme.palette.chart.c2}60`} 
                      fillOpacity={0.6} 
                    />
                    <Legend />
                    <RechartsTooltip 
                      contentStyle={{ 
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`
                      }}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* 차트 섹션 - 세 번째 행 */}
      <Grid container spacing={2}>
        {/* 활동별 비교 차트 */}
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardHeader 
              title="활동별 배출량 비교" 
              titleTypographyProps={{ variant: 'subtitle1', fontWeight: 'medium' }}
              action={
                <IconButton aria-label="settings" size="small">
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              }
              sx={{ p: 2, pb: 1 }}
            />
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ height: 240 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={activityComparisonData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    layout="vertical"
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.background.lighter} />
                    <XAxis 
                      type="number"
                      tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                      axisLine={{ stroke: theme.palette.divider }}
                      unit=" tCO₂e"
                    />
                    <YAxis 
                      dataKey="name"
                      type="category"
                      tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                      axisLine={{ stroke: theme.palette.divider }}
                    />
                    <RechartsTooltip 
                      contentStyle={{ 
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`
                      }}
                    />
                    <Legend />
                    <Bar dataKey="이번달" fill={theme.palette.primary.main} radius={[0, 4, 4, 0]} barSize={15} />
                    <Bar dataKey="지난달" fill={theme.palette.chart.c2} radius={[0, 4, 4, 0]} barSize={15} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* 산점도 차트 */}
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardHeader 
              title="배출량 상관관계 분석" 
              titleTypographyProps={{ variant: 'subtitle1', fontWeight: 'medium' }}
              action={
                <IconButton aria-label="settings" size="small">
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              }
              sx={{ p: 2, pb: 1

               }}
            />
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ height: 240 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ScatterChart
                    margin={{ top: 15, right: 30, left: 20, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.background.lighter} />
                    <XAxis 
                      type="number" 
                      dataKey="x" 
                      name="사용량" 
                      unit=" kWh"
                      tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                      axisLine={{ stroke: theme.palette.divider }}
                    />
                    <YAxis 
                      type="number" 
                      dataKey="y" 
                      name="배출량" 
                      unit=" tCO₂e"
                      tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                      axisLine={{ stroke: theme.palette.divider }}
                    />
                    <ZAxis 
                      type="number" 
                      dataKey="z" 
                      range={[50, 400]} 
                      name="효율성" 
                      unit="%" 
                    />
                    <RechartsTooltip 
                      cursor={{ strokeDasharray: '3 3' }}
                      contentStyle={{ 
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`
                      }}
                      formatter={(value, name, props) => {
                        if (name === 'z') return [`${value}%`, '효율성'];
                        if (name === 'x') return [`${value} kWh`, '사용량'];
                        if (name === 'y') return [`${value} tCO₂e`, '배출량'];
                        return [value, name];
                      }}
                    />
                    <Legend />
                    <Scatter 
                      name="월별 데이터" 
                      data={scatterData} 
                      fill={theme.palette.primary.main} 
                      shape="circle"
                    />
                  </ScatterChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* 게이지 차트 */}
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ height: '100%', minHeight: 160 }}>
            <CardHeader 
              title="주요 지표" 
              titleTypographyProps={{ variant: 'subtitle1', fontWeight: 'medium' }}
              action={
                <IconButton aria-label="settings" size="small">
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              }
              sx={{ p: 2, pb: 1 }}
            />
            <CardContent sx={{ p: 2}}>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <GaugeChart 
                    value={8.0} 
                    maxValue={10} 
                    title="에너지 효율성" 
                    color={theme.palette.primary.main}
                  />
                </Grid>
                <Grid item xs={4}>
                  <GaugeChart 
                    value={15.0} 
                    maxValue={20} 
                    title="교통 배출량" 
                    color={theme.palette.chart.c2}
                  />
                </Grid>
                <Grid item xs={4}>
                  <GaugeChart 
                    value={6.5} 
                    maxValue={10} 
                    title="식품 배출량" 
                    color={theme.palette.chart.c3}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ p: 1, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      탄소 중립 달성 예상
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" color={theme.palette.success.main}>
                      2045년
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      현재 추세 기준
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ p: 1, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      연간 감축 목표
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" color={theme.palette.primary.main}>
                      5.2%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      2030년까지
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardPage;
